import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';

// Constants
const CHECKBOX_COL_WIDTH = 50;
const ACTION_COL_WIDTH = 60;
const MIN_COLUMN_WIDTH = 80;
const CHAR_WIDTH = 8;
const CELL_PADDING = 20;

// CSS for highlight animation
const HIGHLIGHT_STYLES = `
  @keyframes highlight-pulse {
    0% { background-color: #f8f9fa; }
    50% { background-color: #e2f0fd; border-left: 4px solid #0d6efd; }
    100% { background-color: #f8f9fa; }
  }
  .highlight-new {
    animation: highlight-pulse 1.2s ease-in-out;
  }
  .items-col-resizer {
    position: absolute;
    top: 0;
    right: 0;
    width: 8px;
    height: 100%;
    cursor: col-resize;
    z-index: 2;
    user-select: none;
    touch-action: none;
  }
  .items-col-resizer:hover,
  .items-col-resizer.is-active {
    background: rgba(30, 113, 187, 0.35);
  }
`;

// Utility functions
const calculateColumnWidth = (columnKey, content = [], columnConfigs = {}) => {
  const config = columnConfigs[columnKey];
  const headerText = config?.label || columnKey;

  if (columnKey === 'description') {
    const maxContentLength = content.reduce((max, item) => {
      const desc = item?.description || `Item ${item?.item || item?.skuId || ''}`;
      return Math.max(max, desc.length);
    }, 0);

    const contentBasedWidth = Math.max(headerText.length, maxContentLength) * CHAR_WIDTH + CELL_PADDING;
    return Math.min(Math.max(config?.minWidth || MIN_COLUMN_WIDTH, contentBasedWidth), config?.maxWidth || 350);
  }

  const calculatedWidth = Math.max(MIN_COLUMN_WIDTH, headerText.length * CHAR_WIDTH + CELL_PADDING);
  return Math.min(calculatedWidth, config?.maxWidth || 150);
};

const getColumnStyle = (columnKey, content = [], columnConfigs = {}) => {
  const width = calculateColumnWidth(columnKey, content, columnConfigs);
  return {
    width: `${width}px`,
    minWidth: `${width}px`,
    maxWidth: `${width}px`
  };
};

const getFixedColumnStyle = (width) => ({
  width: `${width}px`,
  minWidth: `${width}px`,
  maxWidth: `${width}px`
});

const RESIZABLE_MIN_WIDTH = 64;
const RESIZABLE_MAX_WIDTH = 900;

const fitColumnWidth = (columnKey, content = [], columnConfigs = {}) => {
  const config = columnConfigs[columnKey] || {};
  const headerText = config.label || columnKey;
  let longest = String(headerText).length;

  content.forEach((item) => {
    const value = item?.[columnKey];
    if (value != null && value !== '') {
      longest = Math.max(longest, String(value).length);
    }
  });

  const raw = longest * 8.5 + 36;
  const min = Math.max(MIN_COLUMN_WIDTH, config.minWidth || MIN_COLUMN_WIDTH);
  return Math.max(min, Math.min(raw, 420));
};

const resizableCellStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

// Custom hooks
const useColumnFilters = (initialFilters = {}) => {
  const [filters, setFilters] = useState(initialFilters);

  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(Object.fromEntries(Object.keys(filters).map(k => [k, ''])));
  }, [filters]);

  return { filters, updateFilter, clearFilters };
};

const useTableState = () => {
  const [collapsedTables, setCollapsedTables] = useState(new Set());
  const [selectedRows, setSelectedRows] = useState({});

  const toggleTable = useCallback((tableId) => {
    setCollapsedTables(prev => {
      const next = new Set(prev);
      if (next.has(tableId)) {
        next.delete(tableId);
      } else {
        next.add(tableId);
      }
      return next;
    });
  }, []);

  const toggleRow = useCallback((tableId, index, checked) => {
    setSelectedRows(prev => {
      const set = new Set(prev[tableId] || []);
      if (checked) {
        set.add(index);
      } else {
        set.delete(index);
      }
      return { ...prev, [tableId]: set };
    });
  }, []);

  const toggleSelectAll = useCallback((tableId, count, checked) => {
    setSelectedRows(prev => ({
      ...prev,
      [tableId]: checked ? new Set(Array.from({ length: count }, (_, i) => i)) : new Set()
    }));
  }, []);

  return {
    collapsedTables,
    selectedRows,
    setSelectedRows,
    toggleTable,
    toggleRow,
    toggleSelectAll
  };
};

// Table Header Component
const TableHeader = React.memo(({
  allColumns,
  content,
  groupKey,
  selectedRows,
  noResults,
  filters,
  columnConfigs,
  filterInputStyle,
  filterPlaceholders,
  onToggleSelectAll,
  onUpdateFilter,
  onClearFilters,
  isAllSelected,
  isIndeterminate,
  headerActions,
  resizableColumns = false,
  onResizeStart
}) => (
  <thead className="table-light">
    <tr>
      <th style={getFixedColumnStyle(CHECKBOX_COL_WIDTH)}>
        <input
          type="checkbox"
          className="form-check-input"
          ref={el => { if (el) el.indeterminate = isIndeterminate(groupKey, content.length); }}
          checked={isAllSelected(groupKey, content.length)}
          onChange={(e) => onToggleSelectAll(groupKey, content.length, e.target.checked)}
          disabled={noResults}
        />
      </th>
      {allColumns.map(col => (
        <th
          key={col}
          title={columnConfigs[col]?.label || col}
          style={resizableColumns
            ? { ...resizableCellStyle, position: 'relative' }
            : getColumnStyle(col, content, columnConfigs)}
        >
          {columnConfigs[col]?.label || col}
          {resizableColumns && (
            <span
              className="items-col-resizer"
              onPointerDown={(event) => onResizeStart(col, event)}
            />
          )}
        </th>
      ))}
      <th style={getFixedColumnStyle(ACTION_COL_WIDTH)}></th>
    </tr>
    <tr>
      <th style={getFixedColumnStyle(CHECKBOX_COL_WIDTH)}></th>
      {allColumns.map(col => (
        <th
          key={col}
          style={resizableColumns
            ? resizableCellStyle
            : getColumnStyle(col, content, columnConfigs)}
        >
          <input
            className="form-control form-control-sm"
            style={filterInputStyle}
            placeholder={filterPlaceholders[col] || col}
            value={filters[col] || ''}
            onChange={e => onUpdateFilter(col, e.target.value)}
          />
        </th>
      ))}
      <th style={getFixedColumnStyle(ACTION_COL_WIDTH)}>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary w-100"
          style={{ padding: '2px 4px', fontSize: '10px' }}
          onClick={onClearFilters}
          title="Clear all filters"
        >
          Clear
        </button>
      </th>
    </tr>
  </thead>
));

// Table Body Component
const TableBody = React.memo(({
  filteredList,
  noResults,
  totalCols,
  allColumns,
  content,
  groupKey,
  selectedRows,
  readOnly,
  columnConfigs,
  cellRenderer,
  filterInputStyle,
  onToggleRow,
  onCellChange,
  onInputFocus,
  onInputBlur,
  resizableColumns = false
}) => (
  <tbody>
    {noResults ? (
      <tr>
        <td colSpan={totalCols} className="text-center text-muted py-3">No results found</td>
      </tr>
    ) : filteredList.length === 0 ? (
      <tr>
        <td colSpan={totalCols} className="text-center text-muted py-3">No matching rows</td>
      </tr>
    ) : (
      filteredList.map(({ row, originalIndex }) => (
        <TableRow
          key={originalIndex}
          row={row}
          originalIndex={originalIndex}
          allColumns={allColumns}
          content={content}
          groupKey={groupKey}
          selectedRows={selectedRows}
          readOnly={readOnly}
          columnConfigs={columnConfigs}
          cellRenderer={cellRenderer}
          filterInputStyle={filterInputStyle}
          onToggleRow={onToggleRow}
          onCellChange={onCellChange}
          onInputFocus={onInputFocus}
          onInputBlur={onInputBlur}
          resizableColumns={resizableColumns}
        />
      ))
    )}
  </tbody>
));

// Table Row Component
const TableRow = React.memo(({
  row,
  originalIndex,
  allColumns,
  content,
  groupKey,
  selectedRows,
  readOnly,
  columnConfigs,
  cellRenderer,
  filterInputStyle,
  onToggleRow,
  onCellChange,
  onInputFocus,
  onInputBlur,
  resizableColumns = false
}) => {
  const handleCellChange = (column, value) => {
    if (!readOnly && onCellChange) {
      onCellChange(groupKey, originalIndex, column, value);
    }
  };

  return (
    <tr>
      <td style={getFixedColumnStyle(CHECKBOX_COL_WIDTH)}>
        <input
          type="checkbox"
          className="form-check-input"
          checked={selectedRows[groupKey]?.has(originalIndex) ?? false}
          onChange={(e) => !readOnly && onToggleRow(groupKey, originalIndex, e.target.checked)}
          disabled={readOnly}
        />
      </td>
      {allColumns.map(col => (
        <td
          key={col}
          style={resizableColumns
            ? resizableCellStyle
            : getColumnStyle(col, content, columnConfigs)}
          title={typeof row[col] === 'string' ? row[col] : row[col] != null ? String(row[col]) : undefined}
        >
          {cellRenderer ?
            cellRenderer(col, row, originalIndex, {
              readOnly,
              filterInputStyle,
              onCellChange: handleCellChange,
              onInputFocus,
              onInputBlur,
              groupKey
            }) :
            (row[col] || '-')
          }
        </td>
      ))}
      <td style={getFixedColumnStyle(ACTION_COL_WIDTH)}></td>
    </tr>
  );
});

// Table Card Component
const TableCard = React.memo(({
  groupKey,
  headerLabel,
  isExpanded,
  totalTableWidth,
  content,
  filteredList,
  allColumns,
  totalCols,
  filters,
  selectedRows,
  readOnly,
  columnConfigs,
  cellRenderer,
  filterPlaceholders,
  onToggleTable,
  onRemoveGroup,
  onUpdateFilter,
  onClearFilters,
  onToggleSelectAll,
  onToggleRow,
  onCellChange,
  onInputFocus,
  onInputBlur,
  isAllSelected,
  isIndeterminate,
  headerActions,
  hideGroupHeader = false,
  resizableColumns = false,
}) => {
  const noResults = !Array.isArray(content) || content.length === 0;
  const filterInputStyle = resizableColumns
    ? { width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box', padding: '2px 4px' }
    : { width: '100%', boxSizing: 'border-box', padding: '2px 4px' };
  const [columnWidths, setColumnWidths] = useState(() => (
    resizableColumns
      ? Object.fromEntries(allColumns.map((col) => [col, fitColumnWidth(col, content, columnConfigs)]))
      : {}
  ));
  const columnWidthsRef = useRef(columnWidths);
  columnWidthsRef.current = columnWidths;

  useEffect(() => {
    if (!resizableColumns) return undefined;
    setColumnWidths((prev) => {
      const next = { ...prev };
      let changed = false;
      allColumns.forEach((col) => {
        if (next[col] == null) {
          next[col] = fitColumnWidth(col, content, columnConfigs);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    return undefined;
  }, [resizableColumns, allColumns, content, columnConfigs]);

  const startResize = useCallback((columnKey, event) => {
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    const startX = event.clientX;
    const startWidth = columnWidthsRef.current[columnKey] || RESIZABLE_MIN_WIDTH;
    handle.classList.add('is-active');
    const previousCursor = document.body.style.cursor;
    const previousSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (moveEvent) => {
      const next = Math.min(
        RESIZABLE_MAX_WIDTH,
        Math.max(RESIZABLE_MIN_WIDTH, startWidth + (moveEvent.clientX - startX))
      );
      setColumnWidths((prev) => (
        prev[columnKey] === next ? prev : { ...prev, [columnKey]: next }
      ));
    };

    const onUp = () => {
      handle.classList.remove('is-active');
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousSelect;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, []);

  const sizedTableWidth = CHECKBOX_COL_WIDTH + ACTION_COL_WIDTH + allColumns.reduce(
    (sum, col) => sum + (columnWidths[col] || fitColumnWidth(col, content, columnConfigs)),
    0
  );
  const tableWidth = resizableColumns ? sizedTableWidth : totalTableWidth;

  return (
    <div className="card mb-3">
      {!hideGroupHeader && (
      <div
        className="card-header d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
        onClick={() => onToggleTable(groupKey)}
        data-group-key={groupKey}
      >
        <h6 className="mb-0">{headerLabel}</h6>
        <div className="d-flex gap-2">
          {typeof headerActions === 'function' && headerActions({
            groupKey,
            content,
            isExpanded,
            readOnly,
          })}
          <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); onToggleTable(groupKey); }}>
            <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
          </button>
          {!readOnly && onRemoveGroup && (
            <button
              className="btn btn-sm"
              title="Delete"
              onClick={(e) => { e.stopPropagation(); onRemoveGroup(groupKey); }}
            >
              <i className="bi bi-trash"></i>
            </button>
          )}
        </div>
      </div>
      )}

      <div className={`collapse ${isExpanded ? 'show' : ''}`}>
        <div className="card-body p-0">
          <div
            className="table-responsive"
            style={{ overflowX: resizableColumns || totalTableWidth > 1200 ? 'auto' : 'visible' }}
          >
            <table
              className="table table-bordered table-hover mb-0"
              style={resizableColumns ? {
                tableLayout: 'fixed',
                width: `${tableWidth}px`,
                minWidth: `${tableWidth}px`
              } : {
                tableLayout: 'fixed',
                width: '100%',
                minWidth: `${totalTableWidth}px`
              }}
            >
              {resizableColumns && (
                <colgroup>
                  <col style={{ width: CHECKBOX_COL_WIDTH }} />
                  {allColumns.map((col) => (
                    <col
                      key={col}
                      style={{ width: columnWidths[col] || fitColumnWidth(col, content, columnConfigs) }}
                    />
                  ))}
                  <col style={{ width: ACTION_COL_WIDTH }} />
                </colgroup>
              )}
              <TableHeader
                allColumns={allColumns}
                content={content}
                groupKey={groupKey}
                selectedRows={selectedRows}
                noResults={noResults}
                filters={filters}
                columnConfigs={columnConfigs}
                filterInputStyle={filterInputStyle}
                filterPlaceholders={filterPlaceholders}
                onToggleSelectAll={onToggleSelectAll}
                onUpdateFilter={onUpdateFilter}
                onClearFilters={onClearFilters}
                isAllSelected={isAllSelected}
                isIndeterminate={isIndeterminate}
                resizableColumns={resizableColumns}
                onResizeStart={startResize}
              />

              <TableBody
                filteredList={filteredList}
                noResults={noResults}
                totalCols={totalCols}
                allColumns={allColumns}
                content={content}
                groupKey={groupKey}
                selectedRows={selectedRows}
                readOnly={readOnly}
                columnConfigs={columnConfigs}
                cellRenderer={cellRenderer}
                filterInputStyle={filterInputStyle}
                onToggleRow={onToggleRow}
                onCellChange={onCellChange}
                onInputFocus={onInputFocus}
                onInputBlur={onInputBlur}
                resizableColumns={resizableColumns}
              />
            </table>
          </div>
        </div>
      </div>
    </div>
  );
});

// Main ItemsTable Component
const ItemsTable = ({
  data = {},
  columns = [],
  columnConfigs = {},
  filterPlaceholders = {},
  cellRenderer,
  headerLabelFormatter,
  onCellChange,
  headerActions,
  onRemoveGroup,
  onSelectionChange,
  readOnly = false,
  autoSelectOnInput = false,
  filterFunction,
  hideGroupHeader = false,
  resizableColumns = false,
}) => {
  const userInteractingRef = useRef(false);
  const { filters, updateFilter, clearFilters } = useColumnFilters();
  const {
    collapsedTables,
    selectedRows,
    setSelectedRows,
    toggleTable,
    toggleRow,
    toggleSelectAll
  } = useTableState();

  // Memoized filtered data
  const filteredData = useMemo(() => {
    return Object.entries(data || {})
      .filter(([key, val]) => {
        return key !== '__lastAddedTable' &&
          key !== '__userTriggered' &&
          Array.isArray(val) &&
          val.length > 0 &&
          val.some(item => item && (item.skuId || item.item || item.expectedQty > 0));
      });
  }, [data]);

  // Filter function
  const rowMatchesFilters = useCallback((row, originalIndex) => {
    if (filterFunction) {
      return filterFunction(row, originalIndex, filters);
    }

    // Default filter logic
    return Object.entries(filters).every(([key, filterVal]) => {
      if (!filterVal) return true;
      const val = row[key];
      return String(val ?? '').toLowerCase().includes(filterVal.toLowerCase());
    });
  }, [filters, filterFunction]);

  // Handle cell change with auto-selection
  const handleCellChange = useCallback((groupKey, originalIndex, column, value) => {
    userInteractingRef.current = true;

    if (onCellChange) {
      onCellChange(groupKey, originalIndex, column, value);
    }

    if (autoSelectOnInput && column === 'dispatchQty') {
      if (value > 0) {
        toggleRow(groupKey, originalIndex, true);
      } else if (value === 0 || value === '') {
        toggleRow(groupKey, originalIndex, false);
      }
    }

    setTimeout(() => {
      userInteractingRef.current = false;
    }, 1000);
  }, [onCellChange, autoSelectOnInput, toggleRow]);

  // Sync selected rows with data
  useEffect(() => {
    setSelectedRows(prev => {
      const next = {};
      filteredData.forEach(([groupKey, content]) => {
        const existing = prev[groupKey] || new Set();
        const filtered = new Set();
        content.forEach((_, idx) => {
          if (existing.has(idx)) filtered.add(idx);
        });
        next[groupKey] = filtered;
      });
      return next;
    });
  }, [data, filteredData, setSelectedRows]);

  // Emit selection changes
  useEffect(() => {
    if (!onSelectionChange) return;

    const selectedData = Object.fromEntries(
      Object.entries(selectedRows).map(([groupKey, set]) => {
        const dataArray = data[groupKey];
        if (!Array.isArray(dataArray)) {
          return [groupKey, []];
        }

        const rows = Array.from(set || []).map(idx => {
          const row = dataArray[idx];
          return row ? { rowIndex: idx, groupKey, ...row } : null;
        }).filter(Boolean);
        return [groupKey, rows];
      })
    );
    onSelectionChange(selectedData);
  }, [selectedRows, data, onSelectionChange]);

  // Handle new table additions
  useEffect(() => {
    const newKey = data?.__lastAddedTable;
    if (!newKey) return;

    if (collapsedTables.has(newKey)) {
      toggleTable(newKey);
    }

    if (!userInteractingRef.current) {
      setTimeout(() => {
        const header = document.querySelector(`[data-group-key="${newKey}"]`);
        if (header && !userInteractingRef.current) {
          header.scrollIntoView({ behavior: 'smooth', block: 'center' });
          header.classList.add('highlight-new');
          setTimeout(() => {
            header?.classList.remove('highlight-new');
          }, 1200);
        }
      }, 100);
    }
  }, [data, collapsedTables, toggleTable]);

  // Input handlers
  const handleInputFocus = useCallback(() => {
    userInteractingRef.current = true;
  }, []);

  const handleInputBlur = useCallback(() => {
    setTimeout(() => {
      userInteractingRef.current = false;
    }, 500);
  }, []);

  // Helper functions for selection state
  const isAllSelected = useCallback((groupKey, count) =>
    (selectedRows[groupKey]?.size || 0) === count && count > 0, [selectedRows]);

  const isIndeterminate = useCallback((groupKey, count) => {
    const size = selectedRows[groupKey]?.size || 0;
    return size > 0 && size < count;
  }, [selectedRows]);

  // Initialize selection based on dispatchQty when data changes
  useEffect(() => {
    setSelectedRows(prev => {
      const next = {};
      filteredData.forEach(([groupKey, content]) => {
        const existing = prev[groupKey] || new Set();
        const filtered = new Set();
        
        content.forEach((row, idx) => {
          // Auto-select if dispatchQty has a value > 0
          const hasDispatchQty = row.dispatchQty != null && row.dispatchQty > 0;
          
          if (hasDispatchQty || existing.has(idx)) {
            filtered.add(idx);
          }
        });
        
        next[groupKey] = filtered;
      });
      return next;
    });
  }, [data, filteredData, setSelectedRows]);

  return (
    <div className="items-table mt-4">
      <style>{HIGHLIGHT_STYLES}</style>

      {filteredData.map(([groupKey, content]) => {
        const headerLabel = headerLabelFormatter ?
          headerLabelFormatter(groupKey, content) :
          groupKey;

        const filteredList = content
          .map((row, originalIndex) => ({ row, originalIndex }))
          .filter(({ row, originalIndex }) => rowMatchesFilters(row, originalIndex));

        const isExpanded = !collapsedTables.has(groupKey);
        const totalCols = columns.length + 2;

        // Calculate total table width
        const totalTableWidth = CHECKBOX_COL_WIDTH + ACTION_COL_WIDTH +
          columns.reduce((sum, col) => sum + calculateColumnWidth(col, content, columnConfigs), 0);

        return (
          <TableCard
            key={groupKey}
            groupKey={groupKey}
            headerLabel={headerLabel}
            isExpanded={isExpanded}
            totalTableWidth={totalTableWidth}
            content={content}
            filteredList={filteredList}
            allColumns={columns}
            totalCols={totalCols}
            filters={filters}
            selectedRows={selectedRows}
            readOnly={readOnly}
            columnConfigs={columnConfigs}
            cellRenderer={cellRenderer}
            filterPlaceholders={filterPlaceholders}
            onToggleTable={toggleTable}
            onRemoveGroup={onRemoveGroup}
            onUpdateFilter={updateFilter}
            onClearFilters={clearFilters}
            onToggleSelectAll={toggleSelectAll}
            onToggleRow={toggleRow}
            onCellChange={handleCellChange}
            onInputFocus={handleInputFocus}
            onInputBlur={handleInputBlur}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
            headerActions={headerActions}
            hideGroupHeader={hideGroupHeader}
            resizableColumns={resizableColumns}
          />
        );
      })}
    </div>
  );
};

export default React.memo(ItemsTable);
